import { ArrowLeft } from 'lucide-react'
import {
  ALL_INTEGRATIONS,
  INTEGRATION_SYNC_TYPE_LABELS,
  type IntegrationSyncType,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { VENDOR_CONNECTORS } from './vendors'

export interface IntegrationConnectorProps {
  type: string
  mode: 'onboarding' | 'settings'
  onConnected: () => void
  onCancel: () => void
}

function formatList(items: string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * Onboarding sub-page shell. The vendor-specific connect UX lives in one
 * component per integration under ./vendors/. This component is only page
 * chrome — title, security context, back navigation — plus the mount point
 * that looks up the vendor component from VENDOR_CONNECTORS.
 *
 * There is deliberately NO branching by AUTH_TYPE, no inference from registry
 * fields, no per-vendor hardcoded strings. Both the Settings page and this
 * onboarding page mount the SAME vendor components; if a vendor's connect
 * flow changes, one file changes.
 */
export function IntegrationConnector({
  type,
  mode,
  onConnected,
  onCancel,
}: IntegrationConnectorProps) {
  const integration = ALL_INTEGRATIONS.find(i => i.TYPE === type)
  const entry = VENDOR_CONNECTORS[type]
  const VendorComponent = entry?.Component

  if (!integration || !VendorComponent) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-muted-foreground text-sm">
          Unknown integration: {type}
        </p>
        <Button variant="ghost" size="sm" className="mt-4" onClick={onCancel}>
          Back to connections
        </Button>
      </div>
    )
  }

  const supportsSync =
    'SUPPORTS_SYNC' in integration && integration.SUPPORTS_SYNC === true
  const syncTypes: readonly IntegrationSyncType[] =
    'SYNC_TYPES' in integration && Array.isArray(integration.SYNC_TYPES)
      ? (integration.SYNC_TYPES as IntegrationSyncType[])
      : []

  const topDescription =
    supportsSync && syncTypes.length > 0
      ? `Connecting your ${integration.NAME} account will begin syncing ${formatList(
          syncTypes.map(t => INTEGRATION_SYNC_TYPE_LABELS[t].toLowerCase()),
        )}.`
      : `This will be available for pushes back into ${integration.NAME}.`

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-stretch gap-4">
      <div className="mb-2 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · CONNECT
        </p>
        <h1 className="font-space-grotesk mt-2 text-2xl font-semibold tracking-tight">
          Connect {integration.NAME}
        </h1>
      </div>

      <p className="text-muted-foreground text-center text-sm">
        {topDescription}
      </p>

      <VendorComponent
        mode={mode}
        onConnected={onConnected}
        onCancel={onCancel}
      />

      {mode === 'onboarding' && (
        <div className="mt-2 flex justify-center">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Back to connections
          </Button>
        </div>
      )}
    </div>
  )
}
